/**
 * E03-t5 — the guard evaluator: role x command, protected branches, primary format gate.
 *
 * ONE function every surface calls (`evaluateGuard`) so the web app, the CLI and the git host can
 * never disagree about who may do what. It is pure: control-plane facts in, a decision out. The
 * facts come from D1 and from nowhere else — a merged changeset must not be able to rewrite who may
 * merge changesets (Tenancy and Guards q1 archived), so the evaluator reads a CLOSED set of inputs
 * and nothing repository-sourced can reach it even if a caller passes it.
 *
 * A refusal is an ordinary answer with the guard NAMED, plus the remedy that is always open to a
 * reader: capture the intent to the feedback ledger (Tenancy and Guards s2).
 */

export type Role = 'admin' | 'maintainer' | 'viewer';

/**
 * Commands are derived from each KB's own format.yaml (Command Layer f1), so the matrix can never
 * enumerate them all. Every command declares a KIND, and the kind is what a role is measured
 * against when the matrix has no row for the name.
 */
export type CommandKind = 'read' | 'capture' | 'command' | 'merge' | 'govern';

export type GateStatus = 'green' | 'red' | 'unknown';

export interface FormatGate {
  format: string;
  status: GateStatus;
}

export interface Actor {
  userId: string;
  orgId: string;
  role: Role | null;
}

export interface OrgRef {
  orgId: string;
}

export interface KbRef {
  kbId: string;
  orgId: string;
  /** The one format the protected branch answers to (kb_record); the rest report advisory only. */
  primaryFormat: string;
  defaultBranch: string;
}

export interface CommandRequest {
  name: string;
  kind: CommandKind;
  branch?: string;
  gates?: readonly FormatGate[];
}

export interface GuardMatrix {
  /** Per-command role lists from the control plane; absent names fall back to the kind. */
  commands: Readonly<Record<string, readonly Role[]>>;
  protectedBranches: readonly string[];
}

export interface GuardInput {
  actor: Actor;
  org: OrgRef;
  kb: KbRef;
  command: CommandRequest;
  matrix?: GuardMatrix;
}

export interface GuardRemedy {
  action: 'capture';
  command: string;
  description: string;
}

export interface AdvisoryFinding {
  format: string;
  status: GateStatus;
  blocking: false;
}

export interface GuardDecision {
  allowed: boolean;
  /** The guard that refused, named so the refusal can say it out loud. Null when allowed. */
  guard: string | null;
  reason: string;
  remedy: GuardRemedy | null;
  advisory: readonly AdvisoryFinding[];
}

export const ROLES: readonly Role[] = Object.freeze<Role[]>(['admin', 'maintainer', 'viewer']);

export const COMMAND_KINDS: readonly CommandKind[] = Object.freeze<CommandKind[]>([
  'read',
  'capture',
  'command',
  'merge',
  'govern',
]);

/**
 * The Admin page vocabulary, as capabilities: admin governs the org, maintainer holds command and
 * merge rights, viewer reads and captures to the ledger.
 */
export const ROLE_CAPABILITIES: Readonly<Record<Role, readonly CommandKind[]>> = Object.freeze({
  admin: Object.freeze<CommandKind[]>(['read', 'capture', 'command', 'merge', 'govern']),
  maintainer: Object.freeze<CommandKind[]>(['read', 'capture', 'command', 'merge']),
  viewer: Object.freeze<CommandKind[]>(['read', 'capture']),
});

/** Guard names. They appear verbatim in refusals, so they are part of the contract. */
export const GUARDS = Object.freeze({
  MEMBERSHIP: 'membership',
  ROLE_COMMAND: 'role_command',
  PROTECTED_BRANCH: 'protected_branch',
});

/**
 * What a KB gets before anyone touches its guards: main protected, governance pinned to admin, the
 * ledger open to everyone. Named commands only — everything format-derived rides the kind fallback.
 */
export const DEFAULT_GUARD_MATRIX: GuardMatrix = Object.freeze({
  commands: Object.freeze({
    capture: Object.freeze<Role[]>(['admin', 'maintainer', 'viewer']),
    push: Object.freeze<Role[]>(['admin', 'maintainer']),
    merge: Object.freeze<Role[]>(['admin', 'maintainer']),
    'invite-member': Object.freeze<Role[]>(['admin']),
    'remove-member': Object.freeze<Role[]>(['admin']),
    'set-guards': Object.freeze<Role[]>(['admin']),
    // E13-t6 — REDEFINING THE FORMAT IS GOVERNANCE, not authoring. The format
    // decides what every gate MEANS: which documents are valid, which citations
    // resolve, what `green` is. Changing it is the same class of act as
    // `set-guards`, so it sits beside it at admin, and deliberately NOT with the
    // `command` kind a maintainer holds — being allowed to answer a question is
    // not being allowed to change the rules the answer is judged by.
    'set-format': Object.freeze<Role[]>(['admin']),
  }),
  protectedBranches: Object.freeze<string[]>(['main']),
});

/**
 * The closed set of top-level input keys the evaluator reads. Anything else a caller hangs off the
 * input — a guards file from the repo, a tree, a changeset — is invisible here by construction.
 */
export const GUARD_INPUT_KEYS: readonly string[] = Object.freeze([
  'actor',
  'org',
  'kb',
  'command',
  'matrix',
]);

/**
 * Branch protection patterns are exact names, or a `*` glob for families like `release/*`.
 *
 * Exported because the branch-protection QUESTION is asked outside an allow/deny decision too —
 * the git host has to know whether a ref is protected before it decides what a non-fast-forward
 * update to it means (a free branch accepts any push, 0012). Everyone asking must get the same
 * answer, so there is one implementation of the pattern match and this is it.
 */
export function branchIsProtected(matrix: GuardMatrix, branch: string): boolean {
  return matrix.protectedBranches.some((pattern) => {
    if (!pattern.includes('*')) return pattern === branch;
    const rx = pattern
      .split('*')
      .map((part) => part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
      .join('.*');
    return new RegExp(`^${rx}$`).test(branch);
  });
}

/** Own properties only — a command named `toString` must not inherit a role list. */
function rolesFor(matrix: GuardMatrix, name: string): readonly Role[] | undefined {
  return Object.prototype.hasOwnProperty.call(matrix.commands, name)
    ? matrix.commands[name]
    : undefined;
}

/** Every non-primary format that is not green: reported, never blocking (kb_record). */
function advisoryFindings(kb: KbRef, command: CommandRequest): readonly AdvisoryFinding[] {
  const gates = command.gates ?? [];
  return Object.freeze(
    gates
      .filter((gate) => gate.format !== kb.primaryFormat && gate.status !== 'green')
      .map((gate) => Object.freeze({ format: gate.format, status: gate.status, blocking: false as const })),
  );
}

function deny(
  guard: string,
  reason: string,
  advisory: readonly AdvisoryFinding[],
  remedy: GuardRemedy | null = null,
): GuardDecision {
  return { allowed: false, guard, reason, remedy, advisory };
}

/**
 * Evaluate one request. Order is fixed: membership, then role x command, then the protected branch.
 * Each stage answers a different question, and the first one that refuses is the one named.
 */
export function evaluateGuard(input: GuardInput): GuardDecision {
  const { actor, org, kb, command } = input;
  const matrix = input.matrix ?? DEFAULT_GUARD_MATRIX;
  const advisory = advisoryFindings(kb, command);

  // 1. Membership — the control plane decides who is inside the org, for this org's KBs only.
  if (actor.role === null || !ROLES.includes(actor.role)) {
    return deny(
      GUARDS.MEMBERSHIP,
      `${GUARDS.MEMBERSHIP}: ${actor.userId} holds no role in ${org.orgId}`,
      advisory,
    );
  }
  if (actor.orgId !== org.orgId) {
    return deny(
      GUARDS.MEMBERSHIP,
      `${GUARDS.MEMBERSHIP}: ${actor.userId} belongs to ${actor.orgId}, not ${org.orgId}`,
      advisory,
    );
  }
  if (kb.orgId !== org.orgId) {
    return deny(
      GUARDS.MEMBERSHIP,
      `${GUARDS.MEMBERSHIP}: ${kb.kbId} belongs to ${kb.orgId}, not ${org.orgId}`,
      advisory,
    );
  }

  // 2. Role x command — the per-KB matrix is the authority when it names the command; otherwise the
  //    role's capabilities decide by kind, because format-derived commands are never all listed.
  const listed = rolesFor(matrix, command.name);
  const permitted = listed
    ? listed.includes(actor.role)
    : ROLE_CAPABILITIES[actor.role].includes(command.kind);
  if (!permitted) {
    return deny(
      GUARDS.ROLE_COMMAND,
      `${GUARDS.ROLE_COMMAND}: ${actor.role} may not run ${command.name} (${command.kind}) on ${kb.kbId}`,
      advisory,
      captureRemedy(actor.role, command),
    );
  }

  // 3. Protected branch — a merge or direct push onto one needs the PRIMARY format gate green, and
  //    fails closed if that gate is missing or unknown. Every other branch accepts any push
  //    (Git Host f4). Advisory formats are reported and never consulted.
  if (command.kind === 'merge' && command.branch !== undefined && branchIsProtected(matrix, command.branch)) {
    const gate = (command.gates ?? []).find((g) => g.format === kb.primaryFormat);
    if (gate === undefined || gate.status !== 'green') {
      const state = gate === undefined ? 'not reported' : gate.status;
      return deny(
        GUARDS.PROTECTED_BRANCH,
        `${GUARDS.PROTECTED_BRANCH}: ${command.branch} requires a green ${kb.primaryFormat} gate, ${state}`,
        advisory,
      );
    }
  }

  return {
    allowed: true,
    guard: null,
    reason: `${actor.role} may ${command.name} on ${kb.kbId}`,
    remedy: null,
    advisory,
  };
}

/**
 * A role refusal always leaves one door open: say it to the ledger instead. Only a refusal a role
 * could route around this way gets the offer — a red gate is not something capture fixes, and
 * capture cannot be its own remedy.
 */
function captureRemedy(role: Role, command: CommandRequest): GuardRemedy | null {
  if (command.kind === 'capture' || !ROLE_CAPABILITIES[role].includes('capture')) return null;
  return {
    action: 'capture',
    command: 'capture',
    description: `Capture "${command.name}" to the feedback ledger for a maintainer to run.`,
  };
}

/**
 * Load a KB's matrix from D1 (guard_rules, protected_branches). Rows override the default per
 * command name and, if any exist, replace the protected branch list wholesale; a KB nobody has
 * configured gets DEFAULT_GUARD_MATRIX. This is the only way a matrix enters the evaluator.
 */
export async function loadGuardMatrix(db: D1Database, kbId: string): Promise<GuardMatrix> {
  const [rules, branches] = await Promise.all([
    db
      .prepare('SELECT role, command, allowed FROM guard_rules WHERE kb_id = ? ORDER BY command, role')
      .bind(kbId)
      .all<{ role: Role; command: string; allowed: number }>(),
    db
      .prepare('SELECT pattern FROM protected_branches WHERE kb_id = ? ORDER BY pattern')
      .bind(kbId)
      .all<{ pattern: string }>(),
  ]);

  const commands: Record<string, Role[]> = {};
  for (const row of rules.results ?? []) {
    // A command with any row is fully described by its rows; a denied role is simply left out.
    const roles = (commands[row.command] ??= []);
    if (Number(row.allowed) === 1 && !roles.includes(row.role)) roles.push(row.role);
  }

  const patterns = (branches.results ?? []).map((row) => row.pattern);
  return {
    commands: { ...DEFAULT_GUARD_MATRIX.commands, ...commands },
    protectedBranches: patterns.length > 0 ? patterns : DEFAULT_GUARD_MATRIX.protectedBranches,
  };
}
